import React, { useMemo, useState, useEffect } from "react";
import SkeletonTicketList from "../../../loaders/SkeletonTicketList";
import CopyToClipboard from "../../CopyToClipboard";
import DataTableComponentFrontPagination from "../../common/DataTableComponentFrontPagination";
import { renderRelationship } from "../CallDashboardMappings";

const ContactBlock = ({ loading, contactData = [], error = null, onRetry = null }) => {
    const [search, setSearch] = useState("");
    const [dataFiltered, setDataFiltered] = useState([]);

    // Helper: stringify all row values for search-all-fields
    const rowToSearchText = (row) => {
        if (!row || typeof row !== 'object') return '';
        return Object.values(row)
            .map((val) => {
                if (val === null || val === undefined) return '';
                if (typeof val === 'object') {
                    try { return JSON.stringify(val); } catch { return ''; }
                }
                return String(val);
            })
            .join(' ')
            .toLowerCase();
    };
    
    useEffect(() => {
        if (search === "" || search === null) {
            setDataFiltered(contactData)
        } else {
            const term = search.trim().toLowerCase();
            setDataFiltered(contactData.filter((item) => rowToSearchText(item).includes(term)));
        }
    }, [search, contactData])

    const columns = useMemo(() => [
        {
            name: "Name",
            selector: "name",
            sortable: true,
            grow: 1.5,
            cell: (row) => (<div className='line-clamp-2 table-name-column' title={row.name}>{row.name ?? "-"}</div>)
        },
        {
            name: "Relationship",
            selector: "category",
            sortable: true,
            grow: 1,
            cell: (row) => (
                <div>
                    {renderRelationship(row.category)}
                </div>
            )
        },
        {
            name: "Mobile",
            selector: 'mobile',
            sortable: false,
            grow: 1.2,
            cell: (row) => (
                <div className='d-flex align-items-center'>
                    <span className="mr-2">{row.mobile ?? "-"}</span> 
                    {row.mobile && <CopyToClipboard data={row.mobile} />}
                </div>
            )
        },
        {
            name: "Alternative Mobile",
            selector: "alt_mobile",
            sortable: false,
            grow: 1.2,
            cell: (row) => (
                <div className='d-flex align-items-center'>
                    <span className="mr-2">{row.alt_mobile ?? "-"}</span>
                    {row.alt_mobile && <CopyToClipboard data={row.alt_mobile} />}
                </div>
            )
        },
        {
            name: "Email",
            selector: "email",
            sortable: false,
            grow: 1.5,
            cell: (row) => (
                <div className='d-flex align-items-center'>
                    <span className="textLimit100 mr-2" title={row.email}>{row.email ?? "-"}</span>
                    {row.email && <CopyToClipboard data={row.email} />}
                </div>
            )
        },
    ], []);

    const hasData = Array.isArray(contactData) && contactData.length > 0;
    const hasFilteredData = Array.isArray(dataFiltered) && dataFiltered.length > 0;
    const noMatchesForSearch = hasData && !hasFilteredData && search.trim() !== '';

    return (
        <div className='call-dashboard-table table-card mt-3'>
            <div className="table-header">
                <div className="table-title">
                    <i className="fal fa-users"></i> Contacts
                </div>
                {hasData && (
                    <div className="dataTables_filter">
                        <label>
                            <input
                                type="search"
                                placeholder="Search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                aria-controls="contacts-table"
                            />
                        </label>
                        <div className="filter-eff filter-data-btn ml-2">
                            <button className="filter-buttons" type="button">
                                <i className="fal fa-filter"></i>
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <div className="card-body p-2">
                {loading ? (
                    <div className="p-2"><SkeletonTicketList /></div>
                ) : error && !hasData ? (
                    <div className="empty-state error-state">
                        <i className="fal fa-exclamation-triangle text-danger mr-2"></i>
                        <span className="text-danger">{typeof error === 'string' ? error : 'Failed to load contacts'}</span>
                        {onRetry && (
                            <button 
                                type="button"
                                className="btn btn-sm btn-outline-primary ml-3" 
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onRetry();
                                }}
                            >
                                <i className="fal fa-redo mr-1"></i> Retry
                            </button>
                        )}
                    </div>
                ) : noMatchesForSearch ? (
                    <div className="empty-state">
                        No matches for "{search}"
                    </div>
                ) : hasFilteredData ? (
                    <div className="dataTables_filter">
                        <DataTableComponentFrontPagination
                            columns={columns}
                            isExport={false}
                            defaultSort={{'defaultSortColumn': 'name'}}
                            data={dataFiltered}
                            hideSearch="true"
                            custumcss="border-0"
                            search={null}
                            setSearch={null}
                            otherTableProps={{
                                customStyles: {
                                   
                                    headRow: { style: { minHeight: '40px', borderBottom: '1px solid #eee' } },
                                    rows: { style: { minHeight: '60px', border: "none" } }
                                }
                            }}
                        />
                    </div>
                ) : (
                    <div className="empty-state">
                        There are no records to display
                    </div>
                )}
            </div>
        </div>
    );
};

export default ContactBlock;
